import * as THREE from 'three'

export interface OcctImportMesh {
  id: string
  color: [number, number, number] | null
  attributes: {
    position: { array: Float32Array }
    normal: { array: Float32Array }
    uv?: { array: Float32Array }
    /**
     * Per-vertex tangents (xyzw), only present when the source file supplied them.
     * A normal map needs a tangent frame; three can derive one from screen-space
     * derivatives, but that derivation breaks down where an atlas mirrors its UVs
     * (the left and right halves of a symmetric part sharing one island), which is
     * exactly where authored assets put their tangents to work.
     */
    tangent?: { array: Float32Array }
  }
  index: { array: Uint32Array | Uint16Array }
}

export interface OcctImportJSResult {
  meshes: OcctImportMesh[]
}

export type MaterialPreset = 'default' | 'powder' | 'acrylic' | 'glass' | 'steel' | 'custom'

export type MaterialSide = 'front' | 'back' | 'double'

export interface MaterialProperties {
  preset: MaterialPreset
  color: string
  metalness: number
  roughness: number
  transmission: number
  thickness: number
  ior: number
  opacity: number
  /**
   * How strongly a non-metal surface reflects its surroundings (0..1, glossy = 1).
   *
   * This is the lever for a matte finish like powder coating. A black body gets ALL of
   * its brightness from reflected environment — black albedo contributes nothing — so at
   * full specular a black part renders lighter than a dark grey one that is matte, which
   * is backwards from the real product.
   *
   * Unlike the viewer's envMapIntensity, this travels: the exporter writes it as
   * KHR_materials_specular, so the finish survives into the GLB.
   */
  specularIntensity: number
  /**
   * How much of the surrounding environment the surface picks up.
   *
   * This is the value that has no glTF equivalent, and the one that used to be set behind
   * the user's back — 0.7 on a part, 0.05 on a pattern, and derived from Specular on a
   * logo. Because it scales the ambient a surface receives and not just its reflections,
   * those hidden differences meant the same hex landed in three different places. It is a
   * control now; nothing derives it.
   */
  envMapIntensity: number
  emissiveColor: string
  emissiveIntensity: number
  side: MaterialSide
  showUvTestText: boolean
  showUvTestPattern: boolean
  /**
   * Optional decorative pattern image (data URL) printed over the base color.
   * It is composited ON TOP of `color` (alpha-over) and baked into an opaque
   * baseColor texture on export, so the downstream section-wrap shader in the
   * Product Profile viewer — which multiplies the base map (`diffuseColor *= map`)
   * — keeps working without interference. Undefined = no pattern.
   */
  patternImage?: string
  /** Horizontal tiling of the pattern across the UV space (default 1 = wrap once). */
  patternRepeatX?: number
  /** Vertical tiling of the pattern across the UV space (default 1). */
  patternRepeatY?: number
  /** Fine horizontal registration nudge, as a fraction of circumference (-1..1, default 0). Wraps around the seam. */
  patternOffsetX?: number
  /** Fine vertical registration nudge, as a fraction of height (-1..1, default 0). */
  patternOffsetY?: number
  /** Uniform zoom of the pattern for fitting (default 1). */
  patternScale?: number
  /**
   * When true, stretch one copy of the pattern to cover the whole part (may distort).
   * When false/undefined (default), preserve the pattern's aspect ratio (no distortion),
   * tiling as needed — Repeat X controls how many times it wraps around.
   */
  patternFill?: boolean
  /**
   * Vertical placement of the (unstretched) pattern band on the part. Default 'center'.
   * 'object' centers the pattern on the height of another part (patternVAlignRef, or
   * logo_main if unset) — a stable print-zone reference.
   */
  patternVAlign?: 'top' | 'center' | 'bottom' | 'object'
  /** Part id whose center the pattern aligns to when patternVAlign === 'object'. Empty = logo_main. */
  patternVAlignRef?: string
}

export const defaultMaterialProperties: MaterialProperties = {
  preset: 'default',
  color: '#cccccc',
  metalness: 0,
  roughness: 0.5,
  transmission: 0,
  thickness: 0.5,
  ior: 1.5,
  opacity: 1,
  specularIntensity: 1,
  envMapIntensity: 0.7,
  emissiveColor: '#000000',
  emissiveIntensity: 0,
  side: 'front',
  showUvTestText: false,
  showUvTestPattern: false,
}

export const materialPresets: Record<MaterialPreset, Omit<MaterialProperties, 'preset'>> = {
  default: {
    color: '#cccccc',
    metalness: 0,
    roughness: 0.5,
    transmission: 0,
    thickness: 0.5,
    ior: 1.5,
    opacity: 1,
    specularIntensity: 1,
    envMapIntensity: 0.7,
    emissiveColor: '#000000',
    emissiveIntensity: 0,
    side: 'front',
    showUvTestText: false,
    showUvTestPattern: false,
  },
  acrylic: {
    color: '#ffffff',
    metalness: 0,
    roughness: 0,
    transmission: 0.95,
    thickness: 1.0,
    ior: 1.5,
    opacity: 1,
    specularIntensity: 1,
    envMapIntensity: 0.7,
    emissiveColor: '#000000',
    emissiveIntensity: 0,
    side: 'double',
    showUvTestText: false,
    showUvTestPattern: false,
  },
  glass: {
    color: '#ffffff',
    metalness: 0,
    roughness: 0,
    transmission: 0.98,
    thickness: 0.5,
    ior: 1.52,
    opacity: 1,
    specularIntensity: 1,
    envMapIntensity: 0.7,
    emissiveColor: '#000000',
    emissiveIntensity: 0,
    side: 'double',
    showUvTestText: false,
    showUvTestPattern: false,
  },
  steel: {
    color: '#888888',
    metalness: 1,
    roughness: 0.3,
    transmission: 0,
    thickness: 0,
    ior: 1.5,
    opacity: 1,
    specularIntensity: 1,
    envMapIntensity: 1,
    emissiveColor: '#000000',
    emissiveIntensity: 0,
    side: 'front',
    showUvTestText: false,
    showUvTestPattern: false,
  },
  // Powder coating, the finish on most of this drinkware. Matte and barely reflective:
  // a black part has no diffuse term at all, so at the glossy default its entire
  // brightness is reflected studio environment and it renders lighter than a dark grey
  // logo printed on it — backwards from the real product.
  powder: {
    color: '#cccccc',
    metalness: 0,
    roughness: 0.85,
    transmission: 0,
    thickness: 0,
    ior: 1.5,
    opacity: 1,
    specularIntensity: 0.25,
    envMapIntensity: 0.2,
    emissiveColor: '#000000',
    emissiveIntensity: 0,
    side: 'front',
    showUvTestText: false,
    showUvTestPattern: false,
  },
  custom: {
    color: '#cccccc',
    metalness: 0,
    roughness: 0.5,
    transmission: 0,
    thickness: 0.5,
    ior: 1.5,
    opacity: 1,
    specularIntensity: 1,
    envMapIntensity: 0.7,
    emissiveColor: '#000000',
    emissiveIntensity: 0,
    side: 'front',
    showUvTestText: false,
    showUvTestPattern: false,
  },
}

const SIDES: Record<MaterialSide, THREE.Side> = {
  front: THREE.FrontSide,
  back: THREE.BackSide,
  double: THREE.DoubleSide,
}

/**
 * The surface a MaterialProperties describes, as three.js material parameters.
 *
 * Every material this app builds — each preview branch in ThreeObjectMesh and every export
 * factory in utils/export.ts — spreads this and adds only what is structural to it (maps,
 * alphaTest, polygonOffset). They used to list the properties by hand, and each list
 * drifted: one would drop specularIntensity, another hardcode envMapIntensity, so a slider
 * worked on one kind of part and silently did nothing on the next.
 *
 * Everything here is read from `props`. Nothing is hardcoded and no value is borrowed from
 * a different control — that is how the old per-branch overrides crept in.
 */
export function materialParamsFrom(props: MaterialProperties) {
  return {
    // THREE.Color only takes #RRGGBB, so any alpha suffix is stripped.
    color: new THREE.Color(props.color.slice(0, 7)),
    metalness: props.metalness,
    roughness: props.roughness,
    transmission: props.transmission,
    thickness: props.thickness,
    ior: props.ior,
    opacity: props.opacity,
    transparent: props.opacity < 1 || props.transmission > 0,
    emissive: new THREE.Color(props.emissiveColor.slice(0, 7)),
    emissiveIntensity: props.emissiveIntensity,
    specularIntensity: props.specularIntensity,
    envMapIntensity: props.envMapIntensity,
    side: SIDES[props.side] ?? THREE.FrontSide,
  }
}

/**
 * A logo placed on the model as its OWN mesh part (not a UV pattern on the body).
 * The mesh is a curved sheet generated from these values and re-generated whenever
 * one of them changes, so the part can be selected, renamed (e.g. `logo_main`) and
 * exported as a named node the downstream product-profile logic can key off.
 *
 * All lengths are in MODEL units (the same units as `AppObject.offset`); the panel
 * converts them to millimetres for display.
 */
export interface LogoPlacement {
  /** Artwork (data URL). Alpha is preserved — transparent areas are cut out. */
  image: string
  /** Part the logo is wrapped onto. Empty/missing = the largest part. */
  targetId?: string
  /** Angle around the model's Y axis, degrees. 0 = front (+Z), positive turns right. */
  angleDeg: number
  /** Vertical position of the logo's center (model Y). */
  centerY: number
  /** Width across the surface — arc length, not chord. */
  width: number
  /** Height of the logo. */
  height: number
  /** In-plane rotation of the artwork, degrees. */
  rotationDeg: number
  /** Gap between the surface and the sheet; prevents z-fighting. */
  standoff: number
  /** Extrusion along the surface normal. 0 = a single curved sheet (a decal). */
  thickness: number
  /** true = follow the surface; false = a flat plane tangent to it at the center. */
  conform: boolean
  /** Tessellation across the width (default 48). Higher = smoother wrap. */
  segments?: number
  /** Artwork aspect (width/height), used by the panel's "keep proportions" toggle. */
  imageAspect?: number
  /**
   * Drop a white backdrop from the artwork. Defaults on for files with no transparency,
   * which would otherwise sit on the product as a white card.
   */
  knockoutWhite?: boolean
  /**
   * Cut the sheet down to the artwork's own outline instead of leaving it a rectangle.
   *
   * A rectangular sheet only looks like the logo where something reads the texture's
   * transparency. Blender's solid viewport, a viewer that drops alpha, or the old
   * customizer swapping in its own material all draw the rectangle instead. Tracing the
   * silhouette into geometry puts the shape somewhere nothing can ignore it.
   *
   * On for logos placed since this arrived; undefined (older logos) keeps the rectangle,
   * so a file already approved does not change shape on its own.
   */
  cutToShape?: boolean
}

export interface AppObject {
  id: string
  color: [number, number, number]
  mesh: OcctImportMesh
  /**
   * Present when this part is a generated logo sheet. Holds everything needed to
   * rebuild its geometry, so placement stays editable after the fact.
   */
  logo?: LogoPlacement
  customColor?: string
  customTexture?: string
  material?: MaterialProperties
  /**
   * User translation (model units) applied on top of the part's loaded position.
   * Moves the part in the preview and is baked into the export. Undefined = 0,0,0.
   */
  offset?: [number, number, number]
  /** View-only: hide the part in the preview (still exported). Undefined = visible. */
  hidden?: boolean
  /**
   * The `logo_main` part's vertical position expressed in THIS part's pattern-UV
   * space (0 = top, 1 = bottom). Computed at load; lets a pattern center itself on
   * the logo rather than the part's (possibly oversized) bounding box.
   */
  logoV?: number
  /**
   * Original node world-transform (16-float matrix) from a loaded GLB. Used to
   * re-export the part with its original placement instead of baking the
   * transform into the vertices.
   */
  sourceTransform?: number[]
  /**
   * Original local-space geometry from a loaded GLB (before world transform and
   * centering were applied). Re-exporting from this preserves each part's own
   * coordinate frame and name (e.g. so "logo_main" keeps its 0°/180° wrap axis).
   */
  sourceAttributes?: {
    position: Float32Array
    normal?: Float32Array
    uv?: Float32Array
    /** Original tangents, when the source file had them. See OcctImportMesh.attributes.tangent. */
    tangent?: Float32Array
    index?: Uint32Array
  }
  /**
   * The material this part arrived with, kept intact because the source file authored a
   * real PBR surface — a normal, occlusion or metal-rough map, none of which this app can
   * synthesise or represent in `MaterialProperties`.
   *
   * On such a part the appearance IS those maps: a game/scan asset is low-poly geometry
   * carrying 4K textures, so flattening it to a colour and a roughness number throws away
   * everything that makes it recognisable. `material` still applies on top — glTF defines
   * baseColorFactor/metallicFactor/roughnessFactor as MULTIPLIERS over the maps, so the
   * panel's values keep working, with the imported 1.0/1.0/white reading as pass-through.
   *
   * Absent for STEP/STL (no materials at all) and for GLBs this app exported (which only
   * ever write a flattened baseColor `map`) — so those keep the existing behaviour.
   */
  sourceMaterial?: THREE.MeshStandardMaterial
}

/**
 * The unit one model coordinate represents.
 *
 * Everything downstream — the millimetre readouts, the logo placement, the export scale
 * — is derived from this one value. It used to be two independent guesses: the panel
 * inferred millimetres from how big the model was, while the export trusted the unit
 * declared inside the STEP file. They could disagree, and when they did the panel read
 * correctly while the exported GLB came out at the wrong size.
 */
export type ModelUnit = 'mm' | 'cm' | 'm' | 'in'

/** Millimetres per model unit. */
export const MODEL_UNIT_MM: Record<ModelUnit, number> = { mm: 1, cm: 10, m: 1000, in: 25.4 }

export const MODEL_UNIT_LABEL: Record<ModelUnit, string> = {
  mm: 'Millimetres',
  cm: 'Centimetres',
  m: 'Metres',
  in: 'Inches',
}

/**
 * Two units are in play. The FILE unit is what one model coordinate means; it is set when
 * the file loads and fixes the model's real size and the export scale. The DISPLAY unit is
 * the one picked in the panel; it starts as the file unit, and every length is converted
 * into it to be shown and back out of it when typed. Switching it changes the numbers, not
 * the model: 0.011 m reads as 11 mm.
 */
export interface LengthUnits {
  /** Unit lengths are shown and typed in. */
  unit: ModelUnit
  /** Model units → display units. */
  scale: number
}

export function lengthUnits(fileUnit: ModelUnit, displayUnit: ModelUnit): LengthUnits {
  return { unit: displayUnit, scale: MODEL_UNIT_MM[fileUnit] / MODEL_UNIT_MM[displayUnit] }
}

/** Decimals per display unit, for roughly 0.1 mm of resolution: 1 in mm, 4 in metres. */
const MODEL_UNIT_DECIMALS: Record<ModelUnit, number> = { mm: 1, cm: 2, m: 4, in: 3 }

/** Round a length for display in `unit`. `extraDecimals` for fine values (lift, emboss). */
export function roundLength(value: number, unit: ModelUnit, extraDecimals = 0): number {
  const f = 10 ** (MODEL_UNIT_DECIMALS[unit] + extraDecimals)
  return Math.round(value * f) / f
}

/** Spinner step for a length input in `unit` — about 1 mm; `fine` is about 0.05 mm. */
export function lengthStep(unit: ModelUnit, fine = false): number {
  const step = { mm: 1, cm: 0.1, m: 0.001, in: 0.05 }[unit]
  return fine ? step / 20 : step
}

/**
 * Studio settings written into an exported GLB (as glTF `extras` on the exported root) so a
 * re-import comes back the way it was left. Only what is not already carried by the model
 * itself belongs here.
 */
export interface StudioExtras {
  version: 1
  /** The unit sizes were shown in when the file was exported. */
  displayUnit: ModelUnit
}

export const STUDIO_EXTRAS_KEY = 'neatoStudio'

/** The studio settings stored under `root` (or any node below it), if this app wrote the file. */
export function readStudioExtras(root: THREE.Object3D): StudioExtras | undefined {
  let found: StudioExtras | undefined
  root.traverse((o) => {
    if (found) return
    const v = (o.userData as Record<string, unknown> | undefined)?.[STUDIO_EXTRAS_KEY] as StudioExtras | undefined
    if (v?.version === 1 && typeof v.displayUnit === 'string' && v.displayUnit in MODEL_UNIT_MM) found = v
  })
  return found
}

/** A step of the load, for the progress overlay. */
export interface LoadProgress {
  label: string
  /** 0-100. Jumps rather than sweeps: the heavy steps are synchronous and cannot report from inside. */
  percent: number
}

export interface LoaderResult {
  objects: AppObject[]
  vertexCount: number
  viewingScale: number
  /** What one coordinate of this model represents. Drives readouts and export scale. */
  unit: ModelUnit
  /** The unit sizes were last shown in, when the file was exported from here. Else `unit`. */
  displayUnit?: ModelUnit
  /** World-space Y of the model's vertical center, used to aim the camera. */
  verticalCenter?: number
  material?: MaterialProperties
  /**
   * Translation that maps preview coordinates back to the source file's frame
   * (the loader centers the model horizontally and drops it on y=0). Parts created
   * in preview space — e.g. a logo sheet — carry this as their node transform so
   * the faithful GLB re-export places them with the rest of the model.
   */
  sourceOffset?: [number, number, number]
  /**
   * True when the loaded file authored its own PBR maps. The caller uses it to leave the
   * model's UVs alone: a projection would overwrite the atlas those maps are addressed by.
   */
  authoredMaterials?: boolean
}

export type UvProjection = 'box' | 'cylindrical' | 'planar' | 'none'

declare global {
  interface Window {
    occtimportjs: () => Promise<{
      ReadStepFile: (
        fileBuffer: Uint8Array,
        options: {
          createUVs: boolean
          linearDeflection?: number
          angularDeflection?: number
        } | null
      ) => OcctImportJSResult
    }>
  }
}
